import { Component, DestroyRef, computed, effect, inject, input, output, signal, untracked } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { Subscription, forkJoin, of } from "rxjs";
import {
  PlatformAccessAdminDisplayError,
  PlatformEffectivePermission,
  PlatformEffectivePermissionSource,
  PlatformPermissionCatalogItem,
  PlatformUserEffectivePermissions,
  mapPlatformAccessAdminError,
} from "./platform-access-admin.models";
import { PlatformAccessAdminService } from "./platform-access-admin.service";
import { PLATFORM_ACCESS_TERMINOLOGY } from "./platform-access.terminology";

/** What colours a tile: the kinds of source that grant the permission, or none at all. */
export type EffectivePermissionOrigin = "role" | "direct" | "both" | "none";

/** One catalog permission merged with the sources the evaluation reports for it. */
export interface EffectivePermissionTile {
  code: string;
  name: string;
  scopeRequired: boolean;
  origin: EffectivePermissionOrigin;
  granted: boolean;
  fromRole: boolean;
  fromDirect: boolean;
  roleSources: PlatformEffectivePermissionSource[];
  /** "<scopeTypeCode>: <scopeKey>" for every source that carries a scope key, without repeats. */
  scopes: string[];
}

export interface EffectivePermissionCounts {
  granted: number;
  fromRoles: number;
  adHoc: number;
  both: number;
}

export type EffectivePermissionsEmptyState = "no-matches" | "none-granted" | null;

/** The permission behind a toggled tile: what the host needs to grant or revoke it. */
export type EffectivePermissionTarget = Pick<PlatformPermissionCatalogItem, "code" | "name" | "scopeRequired">;

/** Raised when the administrator checks or unchecks a tile's ad hoc grant. */
export interface EffectivePermissionToggle {
  permission: EffectivePermissionTarget;
  /** The checkbox state after the click: true asks for a direct grant, false for its revoke. */
  checked: boolean;
  /** Puts the checkbox and the tile back the way they were; the host calls it when the change fails. */
  revert: () => void;
}

/**
 * # Permissions view
 *
 * Every permission the runtime evaluation grants the selected user, shown on the Access
 * Management page's Permissions tab beside the Roles tab. Every catalog permission is a tile; a
 * tile is coloured by where the ability comes from (a role, an ad hoc direct grant, or both) and
 * lists every source rather than collapsing them. The filter narrows tiles by code or name as the
 * administrator types.
 *
 * Each tile carries a checkbox for its ad hoc grant. Checking an ungranted tile asks the host to
 * grant the permission directly; unchecking a tile with a direct grant asks the host to revoke
 * it, and a tile that a role also grants stays granted through the role. A tile granted only by a
 * role is checked and locked, because role-based abilities are changed on the Roles tab and there
 * is no per-user deny. New direct grants use the global assignment model. The tile recolours as soon as the box is clicked
 * and the next evaluation confirms it; `revert` on the toggle event undoes the click when the
 * host's request fails.
 *
 * The view loads when it is created and again whenever the host changes the user or bumps
 * `reloadTrigger` after an assignment change, and it reloads on Retry after a failure. The tiles
 * stay rendered while the same user's evaluation reloads; the loading message shows only until a
 * user's first evaluation arrives.
 *
 * The legend, counts, pills, state titles, messages and buttons read through the vocabulary in
 * platform-access.terminology.ts (ADO #1542); permission and role names, codes and scope text
 * arrive from the API and are rendered as received.
 */
@Component({
  selector: "phms-platform-access-user-effective-permissions",
  imports: [TerminologyIdentityDirective],
  templateUrl: "./platform-access-user-effective-permissions.component.html",
  styleUrl: "./platform-access-user-effective-permissions.component.scss",
})
export class PlatformAccessUserEffectivePermissionsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformAccessAdminService = inject(PlatformAccessAdminService);
  private loadSubscription?: Subscription;
  private loadRequestId = 0;
  private loadedUserId: number | null = null;

  readonly userId = input.required<number>();
  readonly displayName = input("");
  readonly isActive = input(true);
  /** The host bumps this after a role or direct-grant change so the view shows the new evaluation. */
  readonly reloadTrigger = input(0);
  /** Whether the administrator may add an ad hoc grant; an ungranted tile's checkbox is locked otherwise. */
  readonly canGrant = input(true);
  /** Whether the administrator may revoke an ad hoc grant; a directly granted tile's checkbox is locked otherwise. */
  readonly canRevoke = input(true);
  /** True while the host saves or reloads assignments, which locks every checkbox until it finishes. */
  readonly busy = input(false);

  /** Emits the evaluation each time it loads, so the host can show the granted count on its tab. */
  readonly loaded = output<PlatformUserEffectivePermissions>();
  /** Emits when a tile's checkbox changes, so the host grants or revokes the ad hoc permission. */
  readonly toggle = output<EffectivePermissionToggle>();

  readonly usage = PLATFORM_ACCESS_TERMINOLOGY.effectivePermissions;
  readonly text = createTerminologyUsageSignals(this.usage);
  readonly filter = signal("");
  readonly isLoading = signal(false);
  readonly error = signal<PlatformAccessAdminDisplayError | null>(null);
  readonly catalog = signal<PlatformPermissionCatalogItem[] | null>(null);
  readonly effectivePermissions = signal<PlatformUserEffectivePermissions | null>(null);
  /**
   * Ad hoc grant state the administrator has just toggled, by permission code, shown in place of
   * the loaded evaluation until the next one lands or the host reverts the click.
   */
  private readonly pendingDirectGrants = signal<ReadonlyMap<string, boolean>>(new Map());

  readonly isInactive = computed(() => !this.isActive() || this.effectivePermissions()?.isActive === false);

  readonly tiles = computed(() => {
    const pending = this.pendingDirectGrants();
    return buildTiles(this.catalog() ?? [], this.effectivePermissions()?.permissions ?? [])
      .map((tile) => pending.has(tile.code) ? withDirectGrant(tile, pending.get(tile.code)!) : tile);
  });

  readonly filteredTiles = computed(() => {
    const filter = normalizeSearch(this.filter());
    if (!filter) {
      return this.tiles();
    }

    return this.tiles().filter((tile) =>
      normalizeSearch(tile.code).includes(filter) || normalizeSearch(tile.name).includes(filter),
    );
  });

  readonly counts = computed<EffectivePermissionCounts>(() => {
    const tiles = this.tiles();
    return {
      granted: tiles.filter((tile) => tile.granted).length,
      fromRoles: tiles.filter((tile) => tile.fromRole).length,
      adHoc: tiles.filter((tile) => tile.fromDirect).length,
      both: tiles.filter((tile) => tile.origin === "both").length,
    };
  });

  readonly emptyState = computed<EffectivePermissionsEmptyState>(() => {
    if (this.filter().trim().length > 0) {
      return this.filteredTiles().length === 0 ? "no-matches" : null;
    }

    return this.counts().granted === 0 ? "none-granted" : null;
  });

  constructor() {
    effect(() => {
      const userId = this.userId();
      this.reloadTrigger();
      untracked(() => this.load(userId));
    });
  }

  onFilter(event: Event): void {
    this.filter.set((event.target as HTMLInputElement).value);
  }

  clearFilter(): void {
    this.filter.set("");
  }

  toggleId(tile: EffectivePermissionTile): string {
    return `effective-permission-toggle-${tile.code}`;
  }

  /** A tile granted only by a role is locked: the ability is changed on the Roles tab. */
  isRoleLocked(tile: EffectivePermissionTile): boolean {
    return tile.origin === "role";
  }

  isToggleDisabled(tile: EffectivePermissionTile): boolean {
    if (this.busy() || this.isLoading() || this.isRoleLocked(tile)) {
      return true;
    }

    return tile.granted ? !this.canRevoke() : !this.canGrant();
  }

  /** Why a locked checkbox cannot be changed here; null when the checkbox is live. */
  toggleTitle(tile: EffectivePermissionTile): string | null {
    if (this.isRoleLocked(tile)) {
      return this.text.roleOnlyTitle();
    }

    return null;
  }

  /**
   * Recolours the tile for the requested ad hoc state straight away and asks the host to persist
   * it. The host calls `revert` when its request fails; the next evaluation replaces the
   * optimistic state either way.
   */
  onToggle(tile: EffectivePermissionTile, event: Event): void {
    const source = event.target as HTMLInputElement;
    const checked = source.checked;
    this.setPendingDirectGrant(tile.code, checked);
    // The box shows "granted": a tile a role also grants stays checked (and locks) once its ad hoc grant is unchecked.
    source.checked = checked || tile.fromRole;
    this.toggle.emit({
      permission: { code: tile.code, name: tile.name, scopeRequired: tile.scopeRequired },
      checked,
      revert: () => {
        this.clearPendingDirectGrant(tile.code);
        source.checked = tile.granted;
      },
    });
  }

  /**
   * Loads the permission catalog (once per instance) together with the user's evaluation.
   * A response that arrives after a newer request started is ignored. The current tiles stay
   * rendered while the same user reloads; another user's tiles are cleared so the loading
   * message shows until theirs arrive.
   */
  load(userId: number = this.userId()): void {
    this.loadSubscription?.unsubscribe();
    const requestId = ++this.loadRequestId;
    this.error.set(null);
    this.isLoading.set(true);
    if (this.loadedUserId !== userId) {
      this.loadedUserId = null;
      this.effectivePermissions.set(null);
      this.pendingDirectGrants.set(new Map());
    }

    const catalog = this.catalog();
    this.loadSubscription = forkJoin({
      catalog: catalog ? of(catalog) : this.platformAccessAdminService.listPermissions(),
      effective: this.platformAccessAdminService.getUserEffectivePermissions(userId),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ catalog: loadedCatalog, effective }) => {
          if (requestId !== this.loadRequestId) {
            return;
          }

          this.loadedUserId = userId;
          this.catalog.set(loadedCatalog);
          this.effectivePermissions.set(effective);
          this.pendingDirectGrants.set(new Map());
          this.isLoading.set(false);
          this.loaded.emit(effective);
        },
        error: (error: unknown) => {
          if (requestId !== this.loadRequestId) {
            return;
          }

          this.loadedUserId = null;
          this.effectivePermissions.set(null);
          this.pendingDirectGrants.set(new Map());
          this.isLoading.set(false);
          this.error.set(toDisplayError(error));
        },
      });
  }

  private setPendingDirectGrant(code: string, granted: boolean): void {
    this.pendingDirectGrants.update((pending) => new Map(pending).set(code, granted));
  }

  private clearPendingDirectGrant(code: string): void {
    this.pendingDirectGrants.update((pending) => {
      const next = new Map(pending);
      next.delete(code);
      return next;
    });
  }
}

/**
 * One tile per catalog permission, plus one for any granted permission the active catalog
 * omits, sorted by code so the grid reads the same way as the API response.
 */
function buildTiles(
  catalog: PlatformPermissionCatalogItem[],
  permissions: PlatformEffectivePermission[],
): EffectivePermissionTile[] {
  const grantedByCode = new Map(permissions.map((permission) => [permission.code, permission]));
  const tiles = new Map<string, EffectivePermissionTile>();

  for (const item of catalog) {
    tiles.set(item.code, createTile(item.code, item.name, item.scopeRequired, grantedByCode.get(item.code)?.sources ?? []));
  }

  for (const permission of permissions) {
    if (!tiles.has(permission.code)) {
      tiles.set(permission.code, createTile(permission.code, permission.name, permission.scopeRequired, permission.sources));
    }
  }

  return [...tiles.values()].sort((left, right) => left.code.localeCompare(right.code));
}

function createTile(
  code: string,
  name: string,
  scopeRequired: boolean,
  sources: PlatformEffectivePermissionSource[],
): EffectivePermissionTile {
  return composeTile(
    code,
    name,
    scopeRequired,
    sources.filter((source) => source.kind === "role"),
    sources.filter((source) => source.kind === "direct"),
  );
}

/**
 * The tile as it will read once the administrator's click is persisted: a check adds the global,
 * open-ended direct grant the host creates; an uncheck ends every direct grant, so the tile keeps
 * only what its roles give it.
 */
function withDirectGrant(tile: EffectivePermissionTile, granted: boolean): EffectivePermissionTile {
  const directSources: PlatformEffectivePermissionSource[] = granted
    ? [{ kind: "direct", scopeTypeCode: "global", scopeKey: null }]
    : [];
  return composeTile(tile.code, tile.name, tile.scopeRequired, tile.roleSources, directSources);
}

function composeTile(
  code: string,
  name: string,
  scopeRequired: boolean,
  roleSources: PlatformEffectivePermissionSource[],
  directSources: PlatformEffectivePermissionSource[],
): EffectivePermissionTile {
  const fromRole = roleSources.length > 0;
  const fromDirect = directSources.length > 0;
  const scopes = [...new Set(
    [...roleSources, ...directSources]
      .filter((source) => (source.scopeKey ?? "").trim().length > 0)
      .map((source) => `${source.scopeTypeCode ?? "scope"}: ${source.scopeKey}`),
  )];

  return {
    code,
    name,
    scopeRequired,
    origin: fromRole && fromDirect ? "both" : fromRole ? "role" : fromDirect ? "direct" : "none",
    granted: fromRole || fromDirect,
    fromRole,
    fromDirect,
    roleSources,
    scopes,
  };
}

function normalizeSearch(value: string | null | undefined): string {
  return (value ?? "").trim().toLocaleLowerCase();
}

function toDisplayError(error: unknown): PlatformAccessAdminDisplayError {
  if (error && typeof error === "object" && Array.isArray((error as PlatformAccessAdminDisplayError).messages)) {
    return error as PlatformAccessAdminDisplayError;
  }

  return mapPlatformAccessAdminError(error);
}
